import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components';
import { useUser, useAuth } from '../context';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight from '../hooks/useTopBarHeight';
import { PageHero, SectionHead, RingMark, usePageToast } from '../components/pagesystem/PageSystem';
import '../components/start/start.css';

/**
 * START SOMETHING — step 1 of creating anything (handoff: design_handoff_guest_start_calendar/
 * Start.dc.html). Three option cards (OPEN A PORTAL · HOST A PARTY · FLOAT A PARTY STARTER),
 * the Portal composer under them when Portal is picked, WHAT HAPPENS WHEN YOU POST, and a
 * sticky selection bar carrying CONTINUE / PICK WHO'S IN.
 *
 * Below 980px tapping Host a party or Party Starter goes straight into that flow; tapping
 * Portal selects it so the composer opens. "Send a Signal / Send a Portal" are gone: the
 * first option is OPEN A PORTAL, the same words as the City Portal.
 */
const CheckGlyph = ({ size = 20, width = 2.25 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="square" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
);
const StarterGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="square" aria-hidden="true"><path d="M4 6h16M8 6v14M15 6v14M15 13h4" /></svg>
);

export const OPTIONS = [
  {
    key: 'portal',
    kicker: 'RIGHT NOW',
    title: 'OPEN A PORTAL',
    blurb: "Open a Portal when you're free to meet. Your people see it and can opt in to join you.",
    tags: ['LIVE 4 HRS', "YOU PICK WHO'S IN", 'NO MONEY'],
    meta: 'FRIENDS ONLY · NO MONEY',
    to: '/portals/new',
    tone: '#00FFFF',
    how: [
      ['IT OPENS', "It jumps to the top of your people's scrolls, marked fresh."],
      ['THEY OPT IN', 'Anyone you picked can tap in. You see who’s coming.'],
      ['IT CLOSES', 'Four hours later it closes on its own. Nothing to clean up.'],
    ],
  },
  {
    key: 'party',
    kicker: 'ON THE CALENDAR',
    title: 'HOST AN EVENT',
    blurb: 'Greenlit the moment you post. Your price + $1, or free.',
    tags: ['INSTANT', 'TICKETED OR FREE'],
    meta: 'INSTANT · TICKETED OR FREE',
    to: '/host/events/create',
    tone: '#FFF8EA',
    how: [
      ['YOU POST', "It jumps to the top of your friends' scrolls, marked fresh."],
      ['THEY BUY IN', 'Tickets sell at your price + $1. Free works too.'],
      ['YOU THROW IT', 'Cancel anytime before the night.'],
    ],
  },
  {
    key: 'starter',
    kicker: 'IF ENOUGH PEOPLE ARE IN',
    title: 'FLOAT A PARTY STARTER',
    blurb: 'Set the floor that makes it worth it. Friends pledge, the pink bar climbs in everyone’s scroll, nobody risks a dime.',
    tags: ['PLEDGES', 'GREENLIT AT FLOOR'],
    meta: 'PLEDGES · GREENLIT AT FLOOR',
    // A Party Starter is an Advanced-only setting, so this lands on the wizard's own path.
    to: '/host/events/create/advanced?funding=party_starter',
    // The one place #FF00AA is used for the starter (handoff token note) — the species colour
    // elsewhere stays #FF00FF.
    tone: '#FF00AA',
    how: [
      ['YOU SET A FLOOR', 'Pick the number that makes it worth throwing.'],
      ['FRIENDS PLEDGE', 'Joins and pledges bump it back to the top of their scrolls.'],
      ['IT GREENLIGHTS', 'No charge until greenlit. Cancel anytime.'],
    ],
  },
];

export const PROMPTS = ["Who's around tonight?", 'Anyone up for a walk?', 'Coffee in 20?', 'Last-minute plans — who’s in?'];

export default function StartPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();
  const mobile = useMediaQuery('(max-width: 979px)');
  const topH = useTopBarHeight();
  const [say, toast] = usePageToast({ bottom: 96, bottomMobile: 150 });
  const [selected, setSelected] = useState(null);
  const [move, setMove] = useState('');
  const picked = OPTIONS.find((o) => o.key === selected) || null;

  const go = (opt) => {
    if (opt.key === 'portal') {
      // The same shape the City Portal's OPEN A PORTAL sends (PortalComposerPage reads
      // `fromSquare.defaultText`); no square is attached from here.
      navigate('/portals/new', { state: { fromSquare: { defaultText: move.trim(), city: null, townSquare: false } } });
      return;
    }
    navigate(opt.to);
  };

  const choose = (opt) => {
    // Phones go straight into the party flows; Portal selects so the composer opens.
    if (mobile && opt.key !== 'portal') { go(opt); return; }
    setSelected(opt.key);
  };

  const cont = () => {
    if (!picked) return;
    if (picked.key === 'portal' && !move.trim()) { say('SAY WHAT THE MOVE IS FIRST'); return; }
    go(picked);
  };

  const how = (picked || OPTIONS[1]).how;
  const howTone = picked ? picked.tone : '#F2F2F2';
  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="ps-page st-page" style={{ '--ps-top': `${topH}px` }}>
      <Navbar user={profile} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/')} />

      <PageHero
        kicker="CREATE · STEP 1 OF 3"
        title="START SOMETHING."
        sub="Pick how you want to gather. You can change it later."
        right={(
          <div className="st-steps" aria-label="Steps">
            {[['1', 'PICK'], ['2', 'DETAILS'], ['3', 'POST']].map(([n, label], i) => (
              <span key={n} className="st-step" data-on={i === 0 ? '' : undefined}><b>{n}</b>{label}</span>
            ))}
          </div>
        )}
      />

      <main className="ps-container ps-main st-main">
        <SectionHead title="CHOOSE ONE" />

        <div className="st-options" role="radiogroup" aria-label="Choose one">
          {OPTIONS.map((o) => {
            const on = selected === o.key;
            return (
              <button
                key={o.key}
                type="button"
                role="radio"
                aria-checked={on}
                className="st-option"
                data-on={on ? '' : undefined}
                data-testid={`option-${o.key}`}
                onClick={() => choose(o)}
              >
                <span className="st-option-top">
                  {o.key === 'portal' ? (
                    <RingMark size={52} ripple inset={4} glyph={20} />
                  ) : (
                    <span className="st-icon" data-party={o.key === 'party' ? '' : undefined} data-starter={o.key === 'starter' ? '' : undefined}>
                      <span>{o.key === 'party' ? <CheckGlyph /> : <StarterGlyph />}</span>
                    </span>
                  )}
                  <span className="st-dot">{on && <CheckGlyph size={12} width={3.5} />}</span>
                </span>
                <span className="st-option-text">
                  <span className="st-option-kicker">{o.kicker}</span>
                  <span className="st-option-title">{o.title}</span>
                  <span className="st-option-blurb">{o.blurb}</span>
                </span>
                <span className="st-tags">{o.tags.map((t) => <span key={t}>{t}</span>)}</span>
              </button>
            );
          })}
        </div>

        {selected === 'portal' && (
          <div className="st-composer" data-testid="portal-composer">
            <div className="st-composer-head">
              <b>OPEN A PORTAL</b>
              <span>LIVE 4 HRS · YOU PICK WHO'S IN</span>
            </div>
            <div className="st-composer-row">
              <input
                value={move}
                onChange={(e) => setMove(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') cont(); }}
                placeholder="What do you want to do?"
                aria-label="What do you want to do?"
                autoComplete="off"
              />
              <button type="button" onClick={cont}>PICK WHO'S IN →</button>
            </div>
            <div className="st-prompts">
              {PROMPTS.map((p) => <button key={p} type="button" onClick={() => setMove(p)}>{p}</button>)}
            </div>
          </div>
        )}

        <div className="st-how" aria-label="What happens when you post">
          {how.map(([label, body], i) => (
            <div key={label} className="st-how-tile">
              <span className="st-how-head">
                <span className="st-how-n" style={{ background: howTone }}>{i + 1}</span>
                <span>{label}</span>
              </span>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </main>

      <div className="st-bar">
        <div className="ps-container st-bar-inner">
          <span className="st-sel" data-none={picked ? undefined : ''}>
            <small>SELECTED</small>
            <b>{picked ? picked.title : 'PICK ONE OF THE THREE ABOVE.'}</b>
          </span>
          <span className="st-bar-right">
            {picked && <span className="st-bar-meta">{picked.meta}</span>}
            <button type="button" className="st-cta" data-ready={picked ? '' : undefined} disabled={!picked} onClick={cont}>
              {picked?.key === 'portal' ? "PICK WHO'S IN" : 'CONTINUE'} <span aria-hidden>→</span>
            </button>
          </span>
        </div>
      </div>
      {toast}
    </div>
  );
}
